import { useMemo, useState } from 'react';
import { useWorkerSourceOptions } from 'corp/hooks/useWorkerSource';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import {
  Tabs,
  TextArea,
  FormField,
  Box,
  Button,
  Text,
  Flex,
  Checkbox,
  Tooltip,
} from '@scale/scaleui-radix';
import useContributorsAdapterCard from './useContributorsAdapterCard';
import { parseAnythingSeparatedIds } from '@scale/shared/utils';
import {
  INPUT_COLUMNS,
  CB_SEARCH_REVAMP_INPUT_COLUMNS,
  FILTER_PRESET_MARKETPLACE,
  FILTER_PRESET_PRIMARY_PROJECTS,
  FILTER_PRESET_ELIGIBLE_DIRECT_ASSIGN_CONTRIBUTORS,
  SCOPING_COLUMNS,
} from './constants';
import { Filters, type ColumnComponentProps } from '@scale/compass-ui/components/filters/Filters';
import { COLUMN_ID_TO_OPTIONS } from 'react-scale/corp/views/DataCompassCardsV2/components/filters/inputFilterOptions';
import { FilterPreset } from '@scale/compass-shared/filter';
import { useUser } from 'react-scale/components/ui/UserContext';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { useFetchAllWorkerSkills } from 'corp/views/CorpProjectGroupManager/communication/apiClient';
import { CohortSelector } from 'corp/views/CorpProjectGroupManager/apps/Allocation/components/Cohorts/CohortSelector';

type ContributorsAdapterCardTab = 'Search' | 'Filter';
const FILTER_PRESETS = [
  FILTER_PRESET_MARKETPLACE,
  FILTER_PRESET_PRIMARY_PROJECTS,
  FILTER_PRESET_ELIGIBLE_DIRECT_ASSIGN_CONTRIBUTORS,
];

const ContributorsAdapterCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const {
    filter,
    setFilter,
    setContributorIdsOrEmails,
    searchMode,
    setSearchMode,
    contributorIdsOrEmails,
    includeAllProjectData,
    setIncludeAllProjectData,
    includeAllWorkerSkillData,
    setIncludeAllWorkerSkillData,
    excludeFraudHolding,
    setExcludeFraudHolding,
  } = useContributorsAdapterCard(index);
  const [currentTab, setCurrentTab] = useState<ContributorsAdapterCardTab>(
    searchMode === 'Search' ? 'Search' : 'Filter',
  );
  const [searchInput, setSearchInput] = useState<string>(contributorIdsOrEmails.join(', '));

  const onTabChange = (val: string) => {
    const tab = val as ContributorsAdapterCardTab;
    setCurrentTab(tab);
    setSearchMode(tab);
  };

  const onSearchInputChange = (input: string) => {
    setSearchInput(input);
    setContributorIdsOrEmails(parseAnythingSeparatedIds(input));
  };

  const appendFilterPreset = (preset: FilterPreset) => {
    setFilter({
      operator: filter.operator || 'and',
      filtersOrFilterGroups: [...filter.filtersOrFilterGroups, preset.filter],
    });
  };

  const { data: workerSkills } = useFetchAllWorkerSkills(false, 0);
  const workerSkillOptions = useMemo(() => {
    return (
      workerSkills?.map(skill => {
        return {
          value: skill.id,
          label: skill.display_name ?? skill.name,
        };
      }) ?? []
    );
  }, [workerSkills]);

  const workerSources = useWorkerSourceOptions({ filterOutCrowd: false });
  const workerSourceOptions = useMemo(() => {
    return workerSources.map(option => ({
      value: option.source,
      label: option.name,
    }));
  }, [workerSources]);

  const user = useUser();
  const cbSearchRevampEnabled = useUnleashFeatureFlag(
    'allocation-cb-search-revamp',
    { userId: user._id },
    false,
  );
  const cohortVersioningEnabled = useUnleashFeatureFlag(
    'allocation-cohorts-versioning',
    { userId: user._id },
    false,
  );

  const CohortFilterComponent = useMemo(
    () => (props: ColumnComponentProps) =>
      (
        <CohortSelector
          value={props.value}
          onValueChange={props.onValueChange}
          disabled={props.disabled}
          isReadOnly={props.isReadOnly}
          allowExactSubscription={false}
        />
      ),
    [],
  );

  return (
    <div className="p-4">
      <div className="space-y-4 mb-3">
        <Tabs.Root onValueChange={onTabChange} value={currentTab} defaultValue="Search">
          <Tabs.List>
            <Tabs.Trigger value="Search">Search</Tabs.Trigger>
            <Tabs.Trigger value="Filter">Filter</Tabs.Trigger>
          </Tabs.List>
          <Box pt="4">
            <Tabs.Content value="Search">
              {currentTab === 'Search' && (
                <FormField.Root size="3">
                  <FormField.Label htmlFor="contributors-search-textarea">
                    Contributor IDs or emails
                  </FormField.Label>
                  <TextArea
                    id="search-textarea"
                    placeholder="Comma (,), space, or newline separated"
                    value={searchInput}
                    onChange={e => onSearchInputChange(e.target.value)}
                  />
                </FormField.Root>
              )}
            </Tabs.Content>
            <Tabs.Content value="Filter">
              {currentTab === 'Filter' && (
                <>
                  <div className="flex flex-row gap-4 pb-4">
                    {FILTER_PRESETS.map((preset, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        onClick={() => {
                          appendFilterPreset(preset);
                        }}
                      >
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                  <Filters
                    columns={Object.values(
                      cbSearchRevampEnabled ? CB_SEARCH_REVAMP_INPUT_COLUMNS : INPUT_COLUMNS,
                    )}
                    filters={filter}
                    setFilters={setFilter}
                    scopingColumns={
                      cbSearchRevampEnabled ? Object.values(SCOPING_COLUMNS) : undefined
                    }
                    columnOptionMap={{
                      ...COLUMN_ID_TO_OPTIONS,
                      workerSkill: workerSkillOptions,
                      workerSource: workerSourceOptions,
                    }}
                    columnComponentMap={
                      cohortVersioningEnabled
                        ? {
                            cohortIds: CohortFilterComponent,
                          }
                        : {}
                    }
                  />
                </>
              )}
            </Tabs.Content>
          </Box>
        </Tabs.Root>
        {cbSearchRevampEnabled && (
          <Flex direction="row" gap="4">
            <Tooltip content="Include a map of project-scoped data with data for all projects that the contributor has worked on. This is a large amount of data and may impact readability.">
              <Text className="cursor-pointer" as="label" size="2">
                <Flex gap="2">
                  <Checkbox
                    size="2"
                    checked={includeAllProjectData}
                    onCheckedChange={setIncludeAllProjectData}
                  />{' '}
                  Include project-scoped data for all projects
                </Flex>
              </Text>
            </Tooltip>
            <Tooltip content="Include a map of worker skill data with data for all worker skills that the contributor has worked on. This is a large amount of data and may impact readability.">
              <Text className="cursor-pointer" as="label" size="2">
                <Flex gap="2">
                  <Checkbox
                    size="2"
                    checked={includeAllWorkerSkillData}
                    onCheckedChange={setIncludeAllWorkerSkillData}
                  />{' '}
                  Include worker skill data for all worker skills
                </Flex>
              </Text>
            </Tooltip>
            <Tooltip content="Filter out contributors who are in fraud holding.">
              <Text className="cursor-pointer" as="label" size="2">
                <Flex gap="2">
                  <Checkbox
                    size="2"
                    checked={excludeFraudHolding}
                    onCheckedChange={setExcludeFraudHolding}
                  />{' '}
                  Exclude contributors in fraud holding
                </Flex>
              </Text>
            </Tooltip>
          </Flex>
        )}
      </div>
    </div>
  );
};

export default ContributorsAdapterCardComponent;
